import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Typography, Paper, Grid, MenuItem, IconButton, Card,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TextField, Stack, CircularProgress, Pagination, Divider, Chip
} from '@mui/material';
import {
    collection, getDocs, query, where, addDoc,
    deleteDoc, doc, updateDoc, onSnapshot, limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../../firebase';
import { Delete, Edit, CloudUpload, Attachment, Assignment, FilterList } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';

const HomeworkAllocation = () => {
    const uid = auth.currentUser?.uid;
    const [schoolId, setSchoolId] = useState('');
    const [activeYear, setActiveYear] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [homeworks, setHomeworks] = useState([]);
    const [allAllocations, setAllAllocations] = useState([]);
    const [teachers, setTeachers] = useState([]);

    // Date Filter State - Defaults to Today
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [page, setPage] = useState(1);
    const rowsPerPage = 6;

    const [formData, setFormData] = useState({
        id: '', title: '', content: '', teacherId: '',
        classId: '', subjectId: '',
        academicYear: '',
        date: new Date().toISOString().split('T')[0],
        attachmentUrl: ''
    });
    const [file, setFile] = useState(null);

    // --- 1. FETCH SCHOOL ID & ACTIVE YEAR ---
    useEffect(() => {
        const fetchInitial = async () => {
            if (!uid) return;
            try {
                // Fetch School ID from Users collection using auth UID
                const userSnap = await getDocs(query(collection(db, 'users'), where('__name__', '==', uid)));
                if (userSnap.empty) {
                    toast.error("User profile not found");
                    return;
                }
                const sId = userSnap.docs[0].data().schoolId;
                setSchoolId(sId);

                // Fetch Active Academic Year
                const yearQuery = query(
                    collection(db, 'academicyears'),
                    where('schoolId', '==', sId),
                    where('status', '==', 'Active'),
                    limit(1)
                );
                const yearSnap = await getDocs(yearQuery);
                if (!yearSnap.empty) {
                    const yName = yearSnap.docs[0].data().name;
                    setActiveYear(yName);
                    setFormData(prev => ({ ...prev, academicYear: yName }));
                }

                // Load Teachers & Allocations
                const [tSnap, aSnap] = await Promise.all([
                    getDocs(query(collection(db, 'users'), where('schoolId', '==', sId), where('role', '==', 'Teacher'))),
                    getDocs(query(collection(db, 'teacher_allocations'), where('schoolId', '==', sId)))
                ]);

                setTeachers(tSnap.docs.map(d => ({ id: d.id, name: d.data().name })));
                setAllAllocations(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

                setLoading(false);
            } catch (err) {
                console.error(err);
                setLoading(false);
            }
        };
        fetchInitial();
    }, [uid]);

    // --- 2. FETCH HOMEWORK LIST (Filtered by Date) ---
    useEffect(() => {
        if (!schoolId || !activeYear) return;

        // Listen to homework for specific school, year, and the selected filter date
        const q = query(
            collection(db, "homework"),
            where("schoolId", "==", schoolId),
            where("academicYear", "==", activeYear),
            where("date", "==", filterDate)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            setHomeworks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setPage(1); // Reset to first page on date change
        });

        return () => unsubscribe();
    }, [schoolId, activeYear, filterDate]);

    // --- 3. DYNAMIC DROPDOWNS ---
    const availableClasses = useMemo(() => {
        if (!formData.teacherId) return [];
        return [...new Set(allAllocations.filter(a => a.teacherId === formData.teacherId).map(a => a.classId))];
    }, [formData.teacherId, allAllocations]);

    const availableSubjects = useMemo(() => {
        if (!formData.teacherId || !formData.classId) return [];
        const match = allAllocations.find(a => a.teacherId === formData.teacherId && a.classId === formData.classId);
        return match ? match.subjects : [];
    }, [formData.teacherId, formData.classId, allAllocations]);

    const handleSave = async () => {
    if (!formData.title || !formData.classId || !formData.teacherId || !formData.subjectId) {
        return toast.warning("Complete all fields");
    }
    setSubmitting(true);
    try {
        let finalUrl = formData.attachmentUrl;
        if (file) {
            const storageRef = ref(storage, `homework/${schoolId}/${Date.now()}_${file.name}`);
            const uploadTask = await uploadBytes(storageRef, file);
            finalUrl = await getDownloadURL(uploadTask.ref);
        }

        const teacherName = teachers.find(t => t.id === formData.teacherId)?.name || '';
        
        // 1. Separate the ID from the rest of the data
        const { id, ...dataToSave } = formData;

        const payload = {
            ...dataToSave, // Save everything EXCEPT the id inside the document
            schoolId,
            teacherName,
            attachmentUrl: finalUrl,
            updatedAt: new Date(),
            academicYear: activeYear
        };

        if (id) {
            // 2. UPDATE existing document using the separated ID
            await updateDoc(doc(db, "homework", id), payload);
            toast.success("Updated Successfully");
        } else {
            // 3. CREATE new document (Firestore generates a new ID)
            await addDoc(collection(db, "homework"), payload);
            toast.success("Assigned Successfully");
        }

        // 4. RESET Form properly
        setFormData({
            id: '', 
            title: '', 
            content: '', 
            teacherId: '', 
            classId: '', 
            subjectId: '', 
            academicYear: activeYear,
            date: filterDate, // Keep the current view date
            attachmentUrl: ''
        });
        setFile(null);
    } catch (e) { 
        console.error("Save Error:", e);
        toast.error("Error saving"); 
    }
    setSubmitting(false);
};
    if (loading) return <Box textAlign="center" mt={10}><CircularProgress /></Box>;

    const handleDelete = async (id) => {
        // 1. Show Confirmation Alert
        const confirmDelete = window.confirm("Are you sure you want to delete this homework assignment?");

        if (confirmDelete) {
            try {
                // 2. FIXED: Reference must be doc(db, "collection", "documentID")
                const docRef = doc(db, "homework", id);
                await deleteDoc(docRef);

                toast.info("Homework deleted successfully");
            } catch (error) {
                console.error("Delete Error:", error);
                toast.error("Failed to delete. Please try again.");
            }
        }
    };


    return (
        <Box sx={{ p: 1.5, bgcolor: '#f4f7f9', minHeight: '100vh' }}>
            <ToastContainer autoClose={1000} />

            {/* Header with Date Filter */}
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#1a237e', color: 'white', display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Assignment />
                    <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1rem' }}>HOMEWORK ALLOCATION</Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ bgcolor: 'white', p: 0.5, px: 1.5, borderRadius: 2 }}>
                    <FilterList sx={{ color: '#1a237e', fontSize: 20 }} />
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 700 }}>VIEW DATE:</Typography>
                    <TextField
                        type="date"
                        size="small"
                        variant="standard"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        InputProps={{ disableUnderline: true, sx: { fontSize: '0.85rem', fontWeight: 700 } }}
                    />
                </Stack>
            </Paper>

            <Grid className="row" spacing={2}>
                {/* Form Section */}
                <Grid item xs={12} className="col-sm-4">
                    <Card sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 900, color: '#1a237e' }}>NEW ASSIGNMENT</Typography>
                        <Stack spacing={2}>
                            <TextField select fullWidth label="Teacher" size="small" value={formData.teacherId}
                                onChange={e => setFormData({ ...formData, teacherId: e.target.value, classId: '', subjectId: '' })}>
                                {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </TextField>
                            <TextField select fullWidth label="Class" size="small" disabled={!formData.teacherId} value={formData.classId}
                                onChange={e => setFormData({ ...formData, classId: e.target.value, subjectId: '' })}>
                                {availableClasses.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                            <TextField select fullWidth label="Subject" size="small" disabled={!formData.classId} value={formData.subjectId}
                                onChange={e => setFormData({ ...formData, subjectId: e.target.value })}>
                                {availableSubjects.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </TextField>
                            <TextField fullWidth label="Title" size="small" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
                            <TextField type="date" label="Assignment Date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
                            <TextField multiline rows={3} label="Details" fullWidth value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })} />

                            <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                                {file ? file.name.slice(0, 10) : "Attachment"}
                                <input type="file" hidden onChange={e => setFile(e.target.files[0])} />
                            </Button>

                            <Button fullWidth variant="contained" onClick={handleSave} disabled={submitting}>
                                {formData.id ? "Update" : "Save Homework"}
                            </Button>
                        </Stack>
                    </Card>
                </Grid>

                {/* List Section */}
                <Grid item xs={12} className="col-sm-8">
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                        <Box p={1.5} sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f8f9fa' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>LIST FOR: {filterDate}</Typography>
                            <Chip label={activeYear} size="small" color="primary" variant="outlined" />
                        </Box>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#eee' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 900 }}>Class/Subject</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Title</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }} align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {homeworks.length === 0 ? (
                                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5, color: '#999' }}>No homework assigned for this date.</TableCell></TableRow>
                                ) : (
                                    homeworks.slice((page - 1) * rowsPerPage, page * rowsPerPage).map((hw) => (
                                        <TableRow key={hw.id}>
                                            <TableCell>
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 900 }}>{hw.classId}</Typography>
                                                <Typography variant="caption" color="primary">{hw.subjectId}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{hw.title}</Typography>
                                                <Typography variant="caption" display="block">By: {hw.teacherName}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                    {hw.attachmentUrl && (
                                                        <IconButton size="small" href={hw.attachmentUrl} target="_blank"><Attachment fontSize="small" color="error" /></IconButton>
                                                    )}
                                                    <IconButton size="small" onClick={() => setFormData(hw)}><Edit fontSize="small" /></IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(hw.id)} // Calls the new function
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        {homeworks.length > rowsPerPage && (
                            <Box p={2} display="flex" justifyContent="center">
                                <Pagination count={Math.ceil(homeworks.length / rowsPerPage)} page={page} onChange={(e, v) => setPage(v)} size="small" color="primary" />
                            </Box>
                        )}
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default HomeworkAllocation;